import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  PlusIcon,
  ClipboardIcon,
  EyeIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { superAdminAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function InviteLinks() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const queryClient = useQueryClient();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    plan: 'starter',
    trial_days: 14,
    max_uses: '',
    expires_at: '',
    notes: '',
  });

  // Fetch invite links
  const { data: inviteLinksData, isLoading } = useQuery(
    ['inviteLinks', includeInactive],
    () => superAdminAPI.getInviteLinks(includeInactive),
    {
      staleTime: 30000,
    }
  );

  const inviteLinks = inviteLinksData?.invite_links || [];

  // Create invite link mutation
  const createMutation = useMutation(
    (data) => superAdminAPI.createInviteLink(data),
    {
      onSuccess: () => {
        toast.success('Invite link created successfully');
        queryClient.invalidateQueries('inviteLinks');
        setShowCreateModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to create invite link');
      },
    }
  );

  // Deactivate invite link mutation
  const deactivateMutation = useMutation(
    (id) => superAdminAPI.deleteInviteLink(id),
    {
      onSuccess: () => {
        toast.success('Invite link deactivated');
        queryClient.invalidateQueries('inviteLinks');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to deactivate invite link');
      },
    }
  );

  // Activate invite link mutation
  const activateMutation = useMutation(
    (id) => superAdminAPI.updateInviteLink(id, { is_active: true }),
    {
      onSuccess: () => {
        toast.success('Invite link activated');
        queryClient.invalidateQueries('inviteLinks');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to activate invite link');
      },
    }
  );

  // Fetch single invite details
  const fetchInviteDetails = async (invite) => {
    try {
      const details = await superAdminAPI.getInviteLink(invite.id);
      setSelectedInvite(details);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to fetch invite details');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      plan: 'starter',
      trial_days: 14,
      max_uses: '',
      expires_at: '',
      notes: '',
    });
  };

  const handleCreate = (e) => {
    e.preventDefault();
    const data = {
      name: formData.name,
      plan: formData.plan,
      trial_days: parseInt(formData.trial_days),
    };

    if (formData.max_uses) {
      data.max_uses = parseInt(formData.max_uses);
    }
    if (formData.expires_at) {
      data.expires_at = new Date(formData.expires_at).toISOString();
    }
    if (formData.notes) {
      data.notes = formData.notes;
    }

    createMutation.mutate(data);
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (invite) => {
    if (!invite.is_active) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Inactive
        </span>
      );
    }
    if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Expired
        </span>
      );
    }
    if (invite.max_uses && invite.current_uses >= invite.max_uses) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Max Uses Reached
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const colors = {
      starter: 'bg-blue-100 text-blue-800',
      pro: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-indigo-100 text-indigo-800',
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
          colors[plan] || colors.starter
        }`}
      >
        {plan}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invite Links</h1>
          <p className="mt-2 text-gray-600">
            Create and manage custom invite links for partners and enterprise clients
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Invite Link
        </button>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="ml-2 text-sm text-gray-600">Show inactive links</span>
        </label>
      </div>

      {/* Invite Links Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {inviteLinks.length === 0 ? (
          <div className="text-center py-12">
            <LinkIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No invite links</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new invite link.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Invite Link
              </button>
            </div>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trial Days
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uses
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inviteLinks.map((invite) => (
                <tr key={invite.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{invite.name}</div>
                      <div className="text-xs text-gray-500 font-mono">
                        {invite.token.substring(0, 12)}...
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getPlanBadge(invite.plan)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invite.trial_days} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invite.current_uses}
                    {invite.max_uses ? ` / ${invite.max_uses}` : ' / unlimited'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(invite)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invite.expires_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => copyToClipboard(invite.invite_url)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Copy Link"
                      >
                        <ClipboardIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => fetchInviteDetails(invite)}
                        className="text-gray-600 hover:text-gray-900"
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {invite.is_active ? (
                        <button
                          onClick={() => {
                            if (window.confirm('Deactivate this invite link?')) {
                              deactivateMutation.mutate(invite.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Deactivate"
                          disabled={deactivateMutation.isLoading}
                        >
                          <NoSymbolIcon className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => activateMutation.mutate(invite.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Activate"
                          disabled={activateMutation.isLoading}
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create Invite Link</h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Acme Corp Partner"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Plan</label>
                    <select
                      value={formData.plan}
                      onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="starter">Starter</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Trial Days</label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={formData.trial_days}
                      onChange={(e) =>
                        setFormData({ ...formData, trial_days: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Max Uses <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Unlimited"
                      value={formData.max_uses}
                      onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Expires <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="date"
                      value={formData.expires_at}
                      onChange={(e) =>
                        setFormData({ ...formData, expires_at: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notes <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    rows="2"
                    placeholder="Internal notes about this invite link..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createMutation.isLoading ? 'Creating...' : 'Create Invite'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedInvite && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Invite Link Details</h3>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedInvite(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>

              <div className="space-y-4">
                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="text-sm text-gray-900">{selectedInvite.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created By</label>
                    <p className="text-sm text-gray-900">{selectedInvite.created_by}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Plan</label>
                    <p className="text-sm text-gray-900 capitalize">{selectedInvite.plan}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Trial Days</label>
                    <p className="text-sm text-gray-900">{selectedInvite.trial_days} days</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Uses</label>
                    <p className="text-sm text-gray-900">
                      {selectedInvite.current_uses}
                      {selectedInvite.max_uses ? ` / ${selectedInvite.max_uses}` : ' / unlimited'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expires</label>
                    <p className="text-sm text-gray-900">
                      {formatDate(selectedInvite.expires_at)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <p className="text-sm">{getStatusBadge(selectedInvite)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created</label>
                    <p className="text-sm text-gray-900">
                      {formatDateTime(selectedInvite.created_at)}
                    </p>
                  </div>
                </div>

                {/* Notes */}
                {selectedInvite.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <p className="text-sm text-gray-600 bg-gray-50 rounded p-2 mt-1">
                      {selectedInvite.notes}
                    </p>
                  </div>
                )}

                {/* Invite URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Invite URL</label>
                  <div className="flex mt-1">
                    <input
                      type="text"
                      readOnly
                      value={selectedInvite.invite_url}
                      className="flex-1 block w-full border border-gray-300 rounded-l-md shadow-sm py-2 px-3 bg-gray-50 text-sm font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(selectedInvite.invite_url)}
                      className="px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100"
                    >
                      <ClipboardIcon className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                </div>

                {/* Usage History */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usage History ({selectedInvite.usages?.length || 0})
                  </label>
                  {selectedInvite.usages && selectedInvite.usages.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto border rounded-md">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Email
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Tenant ID
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                              Used At
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {selectedInvite.usages.map((usage) => (
                            <tr key={usage.id}>
                              <td className="px-4 py-2 text-sm text-gray-900">{usage.email}</td>
                              <td className="px-4 py-2 text-sm text-gray-500 font-mono text-xs">
                                {usage.tenant_id.substring(0, 16)}...
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-500">
                                {formatDateTime(usage.used_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No uses yet</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedInvite(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
